/**
 * Feature Flag Access Guard for Pitch Deck Feature
 * 
 * This component wraps pitch deck pages and:
 * 1. Checks if user has feature access
 * 2. Shows loading state while checking
 * 3. Shows "Feature Not Available" if no access
 * 4. Shows warning banners for test mode / prerequisites
 * 5. Renders children if access granted
 */

import { useEffect, useState, ReactNode } from "react";
import { Link } from "react-router-dom";
import { Loader2, AlertTriangle, Info, ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { checkPitchDeckAccess, PitchDeckAccessResult } from "@/lib/pitch-deck/featureFlags";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DashboardLayout } from "@/components/layout/DashboardLayout";

interface PitchDeckAccessGuardProps {
  children: ReactNode;
}

export function PitchDeckAccessGuard({ children }: PitchDeckAccessGuardProps) {
  const { user, loading: authLoading } = useAuth();
  const [access, setAccess] = useState<PitchDeckAccessResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function checkAccess() {
      // Wait for auth to load
      if (authLoading) return;

      // If no user, set access denied
      if (!user) {
        setAccess({
          hasAccess: false,
          reason: "Please log in to access this feature",
          isIsolatedMode: false,
        });
        setLoading(false);
        return;
      }

      // Check feature access
      const result = await checkPitchDeckAccess(user.id);
      setAccess(result);
      setLoading(false);
    }

    checkAccess();
  }, [user, authLoading]);

  // Loading state
  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 
            className="h-12 w-12 animate-spin text-primary mx-auto mb-4" 
            aria-label="Loading"
          />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Feature not available
  if (!access?.hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full shadow-lg">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-muted flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-muted-foreground" />
            </div>
            <CardTitle className="text-xl">Feature Not Available</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              {access?.reason || "This feature is not available at this time."}
            </p>
            <Button asChild className="w-full">
              <Link to="/curricula">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Learning
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Access granted - render inside the app shell so the sidebar is available
  return (
    <DashboardLayout>
      {/* Test Mode Banner */}
      {access.isIsolatedMode && (
        <div 
          className="bg-yellow-50 border-l-4 border-yellow-400 p-4"
          role="alert"
        >
          <div className="flex items-center max-w-7xl mx-auto">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3 flex-shrink-0" />
            <p className="text-sm text-yellow-800">
              <span className="font-semibold">TEST MODE:</span>{" "}
              This feature is in development and not connected to the LMS yet.
            </p>
          </div>
        </div>
      )}

      {/* Prerequisites Not Met Banner */}
      {access.prerequisitesMet === false && (
        <div 
          className="bg-blue-50 border-l-4 border-blue-400 p-4"
          role="alert"
        >
          <div className="flex items-center max-w-7xl mx-auto">
            <Info className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
            <p className="text-sm text-blue-800">
              <span className="font-semibold">ℹ️ Note:</span>{" "}
              Complete Modules 1-3 to generate your pitch deck (using mock data for now).
            </p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto">
        {children}
      </div>
    </DashboardLayout>
  );
}
